import { getFormErrorMessage } from "@core/utility/get-error-message";
import { RatePerPaxFormRule, RatePerPaxFormSchema } from "@factory/form/form.rule";
import { useFormDefaultRatePerPax } from "@factory/form/form-default-rate-per-pax.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseRatePerPaxModel } from "@factory/model/query-model-rate-per-pax";
import { 
  useQyDeleteRatePerPax, 
  useQyGetRatePerPaxById, 
  useQyUpdateRatePerPax} from "@factory/query/rate-per-pax.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditRatePerPax() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultRatePerPax();
  const navigate = useNavigate();
  const { mapUpdateRatePerPax, mapDeleteRatePerPax } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { ratePerPaxId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID RatePerPax
  const handleGetApiSuccess = (data: GetResponseRatePerPaxModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("activity", responseData?.activity || "");
      setValue("number", responseData?.number);
      setValue("sizeDescription", responseData?.size_description || "");
      setValue("amount", responseData?.amount);
      setValue("totalAmount", responseData?.total_amount);
  };
  const {
    data: ratePerPaxResponse,
    isLoading,
    isError: ratePerPaxError,
  } = useQyGetRatePerPaxById(ratePerPaxId || "", handleGetApiSuccess);
  const ratePerPaxData = ratePerPaxResponse?.data?.[0];

  // API UPDATE RatePerPax
  const handleUpdateApiSuccess = () => {
    showSuccess("RatePerPax updated successfully");
  };
  const { mutate: updateRatePerPax, isLoading: isEditLoading } = useQyUpdateRatePerPax(
    handleUpdateApiSuccess
  );

  // API DELETE RatePerPax
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("RatePerPax deleted successfully");
  };
  const { mutate: deleteRatePerPax } = useQyDeleteRatePerPax(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<RatePerPaxFormSchema>({
    defaultValues: getDefault(ratePerPaxData),
    resolver: zodResolver(RatePerPaxFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: RatePerPaxFormSchema) => {
    if (!ratePerPaxData || !ratePerPaxData.code) {
      throw new Error("No ratePerPax data");
    }

    const formData = mapUpdateRatePerPax(form, ratePerPaxData.code);
    updateRatePerPax(formData);
  };
  const handleValidateError = (err: FieldErrors<RatePerPaxFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!ratePerPaxData || !ratePerPaxData.code) {
      throw new Error("No ratePerPax data found");
    }

    const form = mapDeleteRatePerPax(ratePerPaxData.code);
    deleteRatePerPax(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    ratePerPaxId,
    ratePerPaxData,
    isLoading,
    ratePerPaxError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}